<script setup lang="ts">
const props = defineProps<{
  productLines?: number[]
}>()

const fullTextSearch = ref('')
const debouncedSearch = ref('')
const selectedFileTags = ref<number[]>([])
const selectedProductLines = ref<number[]>([])

const filesRepository = createFilesRepository()

// TODO should get all pages
const { data: allTags, status: fileTagsStatus } = await useLazyAsyncData(() =>
  filesRepository.getAllTags()
)

const fileTags = computed(() => {
  return allTags.value?.items ?? []
})

// TODO should get all pages
const { data: allLinesNames, status: allProductLinesStatus } =
  await useLazyAsyncData(() => createProductsRepository().getAllLinesNames())

const allProductLines = computed(() => {
  return allLinesNames.value?.items ?? []
})

const querySearch = useRouteQuery<string>('s', '')
const queryFileTypes = useRouteQuery('t[]', [], {
  transform: (val) => ([val].flat() || []).map((ft) => Number(ft)),
})
const queryProductLines = useRouteQuery('p[]', [], {
  transform: (val) => ([val].flat() || []).map((pl) => Number(pl)),
})

fullTextSearch.value = querySearch.value
selectedFileTags.value.push(...queryFileTypes.value)
if (props.productLines) {
  selectedProductLines.value = [...props.productLines]
} else {
  selectedProductLines.value.push(...queryProductLines.value)
}

const debouncedUpdateSearch = useDebounceFn(() => {
  debouncedSearch.value = fullTextSearch.value
  page.value = 1
}, 300)

watch(fullTextSearch, () => {
  querySearch.value = fullTextSearch.value
  debouncedUpdateSearch()
})

watch(selectedFileTags, () => {
  queryFileTypes.value = selectedFileTags.value
  page.value = 1
})

watch(selectedProductLines, () => {
  queryProductLines.value = selectedProductLines.value
  page.value = 1
})

const itemsPerPage = ref(50)
const page = ref(1)

const { data: files, status: filesStatus } = await useLazyAsyncData(
  () =>
    filesRepository.getAllPagination(
      page.value,
      itemsPerPage.value,
      selectedProductLines.value,
      debouncedSearch.value,
      selectedFileTags.value
    ),
  {
    watch: [page, selectedFileTags, selectedProductLines, debouncedSearch],
  }
)

const filesElement = ref<HTMLElement | null>()
function scrollToFiles() {
  filesElement.value?.scrollIntoView({ behavior: 'smooth' })
}

onMounted(async () => {
  await nextTick()

  if (queryFileTypes.value.length) {
    // No idea why this isn't working without setTimeout, element is defined, there should not be any CLS, but it is not scrolling
    setTimeout(() => {
      filesElement.value?.scrollIntoView({ behavior: 'smooth' })
    }, 1000)
  }
})

const { visible, index, showImg } = useLightbox()
</script>

<template>
  <div>
    <div class="bg-neutral-50">
      <div class="container py-10">
        <form class="-mb-4 flex flex-wrap">
          <div class="mb-8 w-full">
            <div class="relative flex duration-700">
              <div class="flex w-full flex-col">
                <input
                  id="download-search-text"
                  v-model="fullTextSearch"
                  class="border border-neutral-400 bg-white px-4 py-2.5 text-xl placeholder-neutral-500 focus:shadow-transparent focus:outline-0"
                  type="search"
                  :placeholder="$t('base.download.search.placeholder')"
                />
                <div class="flex gap-1 pt-1 text-neutral-500">
                  {{ $t('base.download.search.fileCount') }}
                  <UiSpinner
                    v-if="filesStatus === 'pending'"
                    class="ml-1 mt-1 h-[18px] w-[18px]"
                  />
                  <div v-else>
                    {{ files.itemCount }}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div
            class="mb-8 w-full"
            :class="{
              'lg:w-1/3 xl:w-1/4': !productLines,
            }"
          >
            <div>
              <label for="searchType" class="text-lg font-bold">
                {{ $t('base.download.search.fileType') }}
              </label>

              <div v-if="fileTagsStatus === 'pending'">
                <div class="mr-5 grid grid-cols-2 gap-5">
                  <UiSkeletonText v-for="index in 8" :key="index" />
                </div>
              </div>
              <div
                :class="
                  !productLines
                    ? 'columns-2 md:columns-3 lg:columns-1'
                    : 'max-sm:columns-2 sm:flex sm:flex-wrap sm:gap-x-10'
                "
                v-else
              >
                <FormCheckbox
                  v-for="fileTag in fileTags"
                  :key="'file-type-' + fileTag.nid"
                  v-model="selectedFileTags"
                  :property-name="fileTag.translationOfNid"
                  parent-name="file-type"
                  :translated-label-right="fileTag.title"
                />
              </div>
            </div>
          </div>

          <div v-if="!productLines" class="mb-8 w-full lg:w-2/3 xl:w-3/4">
            <div>
              <label for="searchSeries" class="text-lg font-bold">
                {{ $t('base.download.search.productLine') }}
              </label>
              <br />

              <div v-if="allProductLinesStatus === 'pending'">
                <div
                  class="mr-5 grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4"
                >
                  <UiSkeletonText v-for="index in 32" :key="index" />
                </div>
              </div>
              <div
                v-else
                class="columns-2 md:columns-3 lg:columns-3 xl:columns-4"
              >
                <FormCheckbox
                  v-for="productLine in allProductLines"
                  :key="'product-line-' + productLine.nid"
                  v-model="selectedProductLines"
                  :property-name="productLine.translationOfNid"
                  parent-name="product-line"
                  :translated-label-right="productLine.title"
                />
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>

    <section id="files" class="py-10">
      <div ref="filesElement" class="container">
        <div v-if="filesStatus === 'pending'">
          <UiSkeletonDownloadPreview />
        </div>
        <div v-else>
          <div
            v-if="files === null || files.length === 0"
            class="py-60 text-center"
          >
            <h2 class="text-4xl font-bold">
              {{ $t('base.download.results.noFiles') }}
            </h2>
          </div>
          <div>
            <div class="data-files flex flex-wrap">
              <div
                v-for="(file, index) in files.items"
                :key="file.nid ?? index"
                class="mb-12 w-full sm:w-1/2 md:w-1/3 lg:w-1/4"
              >
                <DownloadFilePreview
                  :file="file"
                  :all-file-tags="fileTags"
                  @show-image="showImg(index)"
                />
              </div>
            </div>

            <div class="flex justify-end pr-6">
              <UiPagination
                v-model:value="page"
                :items-per-page="itemsPerPage"
                :item-count="files.itemCount"
                :loading="filesStatus === 'pending'"
                @update:value="scrollToFiles"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
    <UiLightbox
      v-model="visible"
      :index="index"
      :images="
        files?.items.map((file) => withHost(file.url + '/2000x2000/FIT')) || []
      "
    />
  </div>
</template>
